/**
 * M28a — a small SVG line for "how did this number move", reused by every analytics card.
 *
 * Hand-rolled on purpose: one polyline, one gradient fill, three text labels. A charting
 * library buys nothing at this size and costs a dependency. Marks follow the dataviz rules —
 * a 2px line, recessive fill, values in TEXT tokens (muted ink), never in the series color;
 * the single series is named by the card title, so there is no legend.
 *
 * `preserveAspectRatio="none"` stretches the fixed 100×32 viewBox to the card, so stroke
 * width is kept visually constant with vector-effect.
 */
export type SparkPoint = { t: string; v: number };

export function Sparkline({
  points,
  ariaLabel,
  formatValue = (value) => String(value),
  className,
}: {
  points: SparkPoint[];
  ariaLabel: string;
  formatValue?: (value: number) => string;
  className?: string;
}) {
  if (points.length === 0) {
    return <p className="text-xs text-muted-foreground">No data yet.</p>;
  }

  const values = points.map((point) => point.v);
  const min = Math.min(...values);
  const max = Math.max(...values);
  // A flat series still deserves a visible line — pad the domain so it draws mid-band
  // instead of dividing by zero.
  const span = max - min || 1;
  const coords = points.map((point, index) => {
    const x = points.length === 1 ? 50 : (index / (points.length - 1)) * 100;
    // SVG y grows downward; 2..30 leaves headroom so the stroke never clips the frame.
    const y = 30 - ((point.v - min) / span) * 28;
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  });
  const last = values.at(-1)!;

  return (
    <div className={className}>
      <svg
        viewBox="0 0 100 32"
        preserveAspectRatio="none"
        role="img"
        aria-label={ariaLabel}
        className="h-16 w-full"
      >
        <polygon
          points={`0,32 ${coords.join(" ")} 100,32`}
          className="fill-primary/10"
        />
        <polyline
          points={coords.join(" ")}
          fill="none"
          vectorEffect="non-scaling-stroke"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          className="stroke-primary"
        />
      </svg>
      <div className="mt-1 flex justify-between text-xs text-muted-foreground">
        <span>min {formatValue(min)}</span>
        <span>max {formatValue(max)}</span>
        <span className="font-medium text-foreground">now {formatValue(last)}</span>
      </div>
    </div>
  );
}
